<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';
import * as XLSX from 'xlsx';

const route = useRoute();

// 학급 데이터 및 로딩 상태
const classData = ref(null);
const loading = ref(true);

// API 호출 함수
const fetchClassResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code, grade_code, school_cls } = route.query;
    
    if (!exam_id || !schul_code || !grade_code || !school_cls) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('Status 페이지 API 요청:', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

    const response = await api.post('/exam/tea/resultClassDetail', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

    if (response.data.success) {
      classData.value = response.data.result;
      console.log('Status 페이지 데이터:', classData.value);
      
      // 일괄 다운로드를 위해 전역에 저장
      window.currentClassData = response.data.result;
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('Status 페이지 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};

// API 데이터를 기반으로 한 학생 리스트
const students = computed(() => {
  if (!classData.value || !classData.value.list) {
    return [];
  }

  return classData.value.list.map(student => {
    // 문해력 원점수 (korsub1~4 합)
    const litSub1 = parseInt(student.korsub1);
    const litSub2 = parseInt(student.korsub2);
    const litSub3 = parseInt(student.korsub3);
    const litSub4 = parseInt(student.korsub4);
    
    // 모든 하위 영역이 NaN이면 빈 값, 아니면 합산 (NaN은 0으로 처리)
    const hasLitData = !isNaN(litSub1) || !isNaN(litSub2) || !isNaN(litSub3) || !isNaN(litSub4);
    const litOriginalScore = hasLitData ? 
      (litSub1 || 0) + (litSub2 || 0) + (litSub3 || 0) + (litSub4 || 0) : '';
    
    // 수리력 원점수 (matsub1~4 합)
    const numSub1 = parseInt(student.matsub1);
    const numSub2 = parseInt(student.matsub2);
    const numSub3 = parseInt(student.matsub3);
    const numSub4 = parseInt(student.matsub4);
    
    // 모든 하위 영역이 NaN이면 빈 값, 아니면 합산 (NaN은 0으로 처리)
    const hasNumData = !isNaN(numSub1) || !isNaN(numSub2) || !isNaN(numSub3) || !isNaN(numSub4);
    const numOriginalScore = hasNumData ? 
      (numSub1 || 0) + (numSub2 || 0) + (numSub3 || 0) + (numSub4 || 0) : '';

    return {
      user_id: student.user_id || '',
      class: `${student.grade_code}-${student.school_cls}`,
      number: parseInt(student.school_num) || 0,
      name: student.name || '',
      litOriginalScore: litOriginalScore,
      litConvertedScore: parseInt(student.korscore) || '',
      numOriginalScore: numOriginalScore,
      numConvertedScore: parseInt(student.matscore) || '',
      litSub1: isNaN(litSub1) ? '' : litSub1,
      litSub2: isNaN(litSub2) ? '' : litSub2,
      litSub3: isNaN(litSub3) ? '' : litSub3,
      litSub4: isNaN(litSub4) ? '' : litSub4,
      numSub1: student.is_mat1_taken === 'N' ? '' : (isNaN(numSub1) ? '' : numSub1),
      numSub2: student.is_mat1_taken === 'N' ? '' : (isNaN(numSub2) ? '' : numSub2),
      numSub3: student.is_mat2_taken === 'N' ? '' : (isNaN(numSub3) ? '' : numSub3),
      numSub4: student.is_mat2_taken === 'N' ? '' : (isNaN(numSub4) ? '' : numSub4),
      litResult: parseInt(student.korlevel) || '',
      numResult: parseInt(student.matlevel) || '',
      paperType: student.paper_type || ''
    };
  });
});

const sortCriteria = ref('none'); 

const sortedStudents = computed(() => {
  const studentsArray = [...students.value]; 

  if (sortCriteria.value === 'none') {
    // 번호 순으로 숫자 정렬
    return studentsArray.sort((a, b) => a.number - b.number);
  } else if (sortCriteria.value === 'totalOriginalScore') {
    return studentsArray.sort((a, b) => {
      const totalScoreA = a.litOriginalScore + a.numOriginalScore;
      const totalScoreB = b.litOriginalScore + b.numOriginalScore;
      return totalScoreB - totalScoreA; 
    });
  }
  return studentsArray;
});

// 학생 개별 결과 조회
const openStudentResult = (student) => {
  const { exam_id } = route.query;
  
  if (!exam_id || !student.user_id) {
    console.error('exam_id 또는 user_id가 없습니다.', { exam_id, user_id: student.user_id });
    return;
  }
  
  const url = `/result/student?exam_id=${exam_id}&user_id=${student.user_id}`;
  window.open(url, '_blank');
};

// 엑셀 다운로드 함수
const downloadExcel = () => {
  if (!classData.value || !classData.value.list) {
    alert('다운로드할 데이터가 없습니다.');
    return;
  }

  // 엑셀 데이터 생성
  const excelData = sortedStudents.value.map(student => {

    // 탈퇴학생인지 확인
    const isWithdrawnStudent = student.name === '탈퇴학생';

    // 응시 여부 판단 (모든 하위 영역이 빈 값이면 미응시)
    const isLiteracyTaken = !(
      (student.litSub1 === '' || student.litSub1 === 0) &&
      (student.litSub2 === '' || student.litSub2 === 0) &&
      (student.litSub3 === '' || student.litSub3 === 0) &&
      (student.litSub4 === '' || student.litSub4 === 0)
    );
    
    const isNumeracyTaken = !(
      (student.numSub1 === '' || student.numSub1 === 0) &&
      (student.numSub2 === '' || student.numSub2 === 0) &&
      (student.numSub3 === '' || student.numSub3 === 0) &&
      (student.numSub4 === '' || student.numSub4 === 0)
    );

    return {
      '학교명': classData.value.schul_nm || '',
      '학년': classData.value.grade_code || '',
      '반': classData.value.school_cls || '',
      '번호': isWithdrawnStudent ? '' : student.number,
      '이름': student.name,
      '문해력응시': isWithdrawnStudent ? '' : (isLiteracyTaken ? 1 : ''),
      '수리력응시': isWithdrawnStudent ? '' : (isNumeracyTaken ? 1 : ''),
      '문해력검사형': isWithdrawnStudent ? '' : (student.paperType || ''),
      '수리력검사형': isWithdrawnStudent ? '' : (student.paperType || ''),
      '문해력원점수': isWithdrawnStudent ? '' : student.litOriginalScore,
      '수리력원점수': isWithdrawnStudent ? '' : student.numOriginalScore,
      '문해력변환점수': isWithdrawnStudent ? '' : student.litConvertedScore,
      '수리력변환점수': isWithdrawnStudent ? '' : student.numConvertedScore,
      '문해력하위영역1': isWithdrawnStudent ? '' : student.litSub1,
      '문해력하위영역2': isWithdrawnStudent ? '' : student.litSub2,
      '문해력하위영역3': isWithdrawnStudent ? '' : student.litSub3,
      '문해력하위영역4': isWithdrawnStudent ? '' : student.litSub4,
      '수리력하위영역1': isWithdrawnStudent ? '' : student.numSub1,
      '수리력하위영역2': isWithdrawnStudent ? '' : student.numSub2,
      '수리력하위영역3': isWithdrawnStudent ? '' : student.numSub3,
      '수리력하위영역4': isWithdrawnStudent ? '' : student.numSub4,
      '문해력결과': isWithdrawnStudent ? '' : student.litResult,
      '수리력결과': isWithdrawnStudent ? '' : student.numResult
    };
  });

  // 워크시트 생성
  const ws = XLSX.utils.json_to_sheet(excelData);
  
  // 워크북 생성
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '하위영역별점수현황');

  // 파일명 생성
  const schoolName = classData.value.schul_nm || '학교';
  const gradeClass = `${classData.value.grade_code}학년${classData.value.school_cls}반`;
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const filename = `${schoolName}_${gradeClass}_하위영역별점수현황_${dateStr}.xlsx`;

  // 파일 다운로드
  XLSX.writeFile(wb, filename);
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  fetchClassResult();
});


const sortOptions = ref([
  {
    label: '번호 순', 
    value: 'none'     
  },
  {
    label: '합산 원점수 순',
    value: 'totalOriginalScore'
  }
]);
</script>

<template>
  <div class="table-container">
    <div class="table-top">
      <div class="title">
        <h5>우리반 하위 영역별 점수 현황</h5>
        <div class="sort-options">       
        <n-select
          id="sortOrder"
          v-model:value="sortCriteria"
          :options="sortOptions"
          style="width:150px;" 
        />
      </div>
      </div>
      <p class="download-button" @click="downloadExcel">하위 영역별 점수 엑셀 다운로드</p>     
    </div>
    <div class="info">
      <div class="text-wrap">
        <p>문해력 하위 영역</p>
        <p>① 어휘 ② 탐색 및 확인 ③ 통합 및 해석 ④ 비판 및 평가</p>
      </div>
      <div class="text-wrap">
        <p>수리력 하위 영역</p>
        <p>① 수와 양 ② 규칙과 관계 ③ 모양과 측정 ④ 자료와 불확실성</p>
      </div>
    </div>    
   
    <table>
      <thead>       
        <tr>
          <th rowspan="3">반</th>
          <th rowspan="3">번호</th> 
          <th rowspan="3">이름</th>
          <th rowspan="3">결과지</th> 
          <th colspan="2">문해력</th> 
          <th colspan="2">수리력</th> 
          <th colspan="4">문해력 하위 영역</th>
          <th colspan="4">수리력 하위 영역</th>
          <th>문해력</th> 
          <th>수리력</th> 
        </tr>        
        <tr>
          <th rowspan="2">원점수</th> 
          <th rowspan="2">변환점수</th> 
          <th rowspan="2">원점수</th> 
          <th rowspan="2">변환점수</th> 
        </tr>
        <tr>
          <th>①</th>
          <th>②</th>
          <th>③</th>
          <th>④</th>
          <th>①</th>
          <th>②</th>
          <th>③</th>
          <th>④</th>
          <th>결과</th>
          <th>결과</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="18" class="text-center py-4">
            데이터를 불러오는 중...
          </td>
        </tr>
        <tr v-else-if="sortedStudents.length === 0">
          <td colspan="18" class="text-center py-4">
            학생 데이터가 없습니다.
          </td>
        </tr>
        <tr v-else v-for="(student, index) in sortedStudents" :key="student.number">
          <td>{{ student.class }}</td>
          <td>{{ student.number }}</td>
          <td>{{ student.name }}</td>
          <td>
            <button 
              v-if="student.name !== '탈퇴학생'" 
              @click="openStudentResult(student)"
            >
              조회
            </button>
          </td> 
          <td>{{ student.name === '탈퇴학생' ? '' : student.litOriginalScore }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litConvertedScore }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numOriginalScore }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numConvertedScore }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litSub1 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litSub2 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litSub3 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litSub4 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numSub1 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numSub2 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numSub3 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numSub4 }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.litResult }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.numResult }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-container {
    background-color: #fff;
    padding: 20px 20px 30px 20px;
    border-radius: 15px;
}

.table-top {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;  
  .title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .download-button {
    background-color: var(--smoe-primary-500);
    color: #fff;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    height: 40px;
    line-height: 40px;
  } 
}
.info {
  display: flex;
  justify-content: space-between;
  text-align: center;  
  padding: 20px 0;
  .text-wrap {
    border: 1px solid #ccc;
    border-radius: 8px;
    width: calc((100% - 16px )/2);
    padding: 20px 10px;
    font-weight: 500;
    &:nth-child(1) {
      border-color: var(--result-light-blue);
      color: var(--result-light-blue);
    }
    &:nth-child(2) {
      border-color: var(--result-light-red);
      color: var(--result-light-red);
    }
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 16px;
}
th, td {
  border: 1px solid #ccc; padding: 8px; vertical-align: middle;
}
th {
  background-color: #f2f2f2; font-weight: 500;
}

button {
  padding: 4px 10px;
  border: 1px solid var(--smoe-primary-500);
  color: var(--smoe-primary-500);
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px; 
}

// 반복문으로 컬럼 너비 처리 (총 18개) 
@for $i from 1 through 18 { 
  // 반, 번호, 이름, 결과지(조회버튼)
  @if $i == 1 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; } // 반
  } @else if $i == 2 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 40px; } // 번호
  } @else if $i == 3 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 80px; } // 이름
  } @else if $i == 4 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 60px; } // 결과지
  } 
  // 문해력/수리력 원점수 변환점수 컬럼 (5번~8번 컬럼)
  @else if $i >= 5 and $i <= 8 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 60px; }
  }
  // 문해력 하위 영역 컬럼 (9번~12번 컬럼)
  @else if $i >= 9 and $i <= 12 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 45px; } 
  }
  // 수리력 하위 영역 컬럼 (13번~16번 컬럼)
  @else if $i >= 13 and $i <= 16 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 45px; } 
  }
  // 문해력/수리력 결과 컬럼 (17번, 18번)
  @else if $i == 17 or $i == 18 { 
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 60px; } 
  }
}

// 셀렉트 박스
.sort-options {  
  display: flex; 
  align-items: center; 

  label {
    font-weight: bold;
    color: #333;
    white-space: nowrap; 
  }

 :deep(.n-base-selection) {
    height: 40px !important; 
    min-height: 40px !important; 
    
    .n-base-selection-label {
      height: 40px !important; 
      line-height: 40px !important; 
      padding: 0 5px 0 15px !important; 
    }      
  }
  :deep(.n-base-selection-input) {padding: 0; }  
}

// 컬럼 색상
$literacy-color: var(--result-light-blue);
$numeracy-color: var( --result-light-red);

tr:nth-child(1) {
  th:nth-child(5), th:nth-child(7), th:nth-child(9)  {color: $literacy-color;}
}
tr:nth-child(1) {
  th:nth-child(6), th:nth-child(8), th:nth-child(10)  {color: $numeracy-color;}
}

@for $i from 1 through 2 {
  tr:nth-child(2) th:nth-child(#{$i}) {color: $literacy-color;}
}
@for $i from 1 through 4 {
  tr:nth-child(3) th:nth-child(#{$i}) {color: $literacy-color;}
}
tr:nth-child(3) th:nth-child(9) {color: $literacy-color;}

@for $i from 3 through 4 {
  tr:nth-child(2) th:nth-child(#{$i}) {color: $numeracy-color; }
}
@for $i from 5 through 8 {
  tr:nth-child(3) th:nth-child(#{$i}) {color: $numeracy-color;}
}
tr:nth-child(3) th:nth-child(10) {color: $numeracy-color;}
</style>